import React, { Component } from 'react';
import { observer } from 'mobx-react';
import { observable, action } from 'mobx';
import {
    Milestone, MilestoneStore,
} from 'store/planning';

interface ProgressData {
    total_tickets: number;
    done_tickets: number;
    in_progress_tickets: number;
    review_tickets: number;
    blocked_tickets: number;
    todo_tickets: number;
    planned_hours: number;
    spent_hours: number;
    progress_pct: number;
}

interface Props {
    milestoneStore: MilestoneStore;
}

@observer
export class MilestoneProgressTab extends Component<Props> {
    @observable progressMap: Record<number, ProgressData> = {};
    @observable loadingMap: Record<number, boolean> = {};

    async componentDidMount() {
        await this.loadAllProgress();
    }

    async componentDidUpdate(prevProps: Props) {
        const prevIds = prevProps.milestoneStore.models.map((m: any) => m.id).join(',');
        const curIds = this.props.milestoneStore.models.map((m: any) => m.id).join(',');
        if (prevIds !== curIds) {
            await this.loadAllProgress();
        }
    }

    @action
    async loadAllProgress() {
        const { milestoneStore } = this.props;
        const results = await Promise.allSettled(
            milestoneStore.models.map(async (m: any) => {
                const id = m.id as number;
                this.loadingMap[id] = true;
                try {
                    const res = await milestoneStore.api.get(`subproject/${id}/progress/`);
                    const data = res.data || res;
                    this.progressMap[id] = typeof data === 'string' ? JSON.parse(data) : data;
                } catch (e) {
                    console.warn(`Failed to load progress for milestone ${id}:`, e);
                } finally {
                    this.loadingMap[id] = false;
                }
            })
        );
        return results;
    }

    getProgress = (milestoneId: number): ProgressData | null => {
        return this.progressMap[milestoneId] || null;
    };

    renderProgressBar = (pct: number) => {
        const clamped = Math.min(100, Math.max(0, pct || 0));
        return (
            <div className="sp-progress-bar-container" data-test-progress-bar>
                <div
                    className="sp-progress-bar-fill"
                    style={{ width: `${clamped}%` }}
                >
                    {clamped > 10 ? `${Math.round(clamped)}%` : ''}
                </div>
            </div>
        );
    };

    renderStatusBreakdown = (data: ProgressData, milestoneId: number) => {
        const items = [
            { key: 'todo', label: 'To Do', count: data.todo_tickets, dotClass: 'sp-status-dot-todo' },
            { key: 'in_progress', label: 'In Progress', count: data.in_progress_tickets, dotClass: 'sp-status-dot-in_progress' },
            { key: 'review', label: 'Review', count: data.review_tickets, dotClass: 'sp-status-dot-review' },
            { key: 'done', label: 'Done', count: data.done_tickets, dotClass: 'sp-status-dot-done' },
            { key: 'blocked', label: 'Blocked', count: data.blocked_tickets, dotClass: 'sp-status-dot-blocked' },
        ];
        return (
            <div className="sp-status-breakdown" data-test-status-breakdown={milestoneId}>
                <div className="sp-status-breakdown-title">Ticket Status Breakdown</div>
                <div className="sp-status-breakdown-grid">
                    {items.map(item => (
                        <div className="sp-status-item" key={item.key}>
                            <span className={`sp-status-dot ${item.dotClass}`} />
                            <span>{item.label}: {item.count}</span>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    renderMilestoneProgress = (milestone: Milestone) => {
        const id = milestone.id as number;
        const data = this.getProgress(id);
        const isLoading = this.loadingMap[id];
        const project = milestone.project as any;

        return (
            <div className="sp-progress-card" key={id} data-test-progress-card={id}>
                <div className="sp-progress-card-header">
                    <div>
                        <div className="sp-progress-card-title">{milestone.name}</div>
                        <div className="sp-milestone-meta">
                            <span className={`sp-badge sp-badge-${milestone.status}`}>{milestone.status}</span>
                            <span className="sp-milestone-type">{milestone.type}</span>
                            {project && <span>{project.name}</span>}
                        </div>
                    </div>
                </div>

                {isLoading && <div className="sp-loading">Loading progress...</div>}

                {data && !isLoading && (
                    <>
                        {this.renderProgressBar(data.progress_pct)}
                        <div className="sp-progress-stats">
                            <div className="sp-stat-box">
                                <div className="sp-stat-value">{data.total_tickets}</div>
                                <div className="sp-stat-label">Total Tickets</div>
                            </div>
                            <div className="sp-stat-box">
                                <div className="sp-stat-value">{data.done_tickets}</div>
                                <div className="sp-stat-label">Done</div>
                            </div>
                            <div className="sp-stat-box">
                                <div className="sp-stat-value">{data.in_progress_tickets}</div>
                                <div className="sp-stat-label">In Progress</div>
                            </div>
                            <div className="sp-stat-box">
                                <div className="sp-stat-value">{data.blocked_tickets}</div>
                                <div className="sp-stat-label">Blocked</div>
                            </div>
                        </div>

                        <div className="sp-hours-comparison" data-test-hours-comparison={id}>
                            <div className="sp-hours-item">
                                <div className="sp-hours-value">{data.planned_hours}h</div>
                                <div className="sp-hours-label">Planned Hours</div>
                            </div>
                            <div className="sp-hours-item">
                                <div className="sp-hours-value">{data.spent_hours}h</div>
                                <div className="sp-hours-label">Spent Hours</div>
                            </div>
                            <div className="sp-hours-item">
                                <div className="sp-hours-value">
                                    {data.planned_hours > 0
                                        ? `${Math.round((data.spent_hours / data.planned_hours) * 100)}%`
                                        : '—'}
                                </div>
                                <div className="sp-hours-label">Used</div>
                            </div>
                        </div>

                        {this.renderStatusBreakdown(data, id)}
                    </>
                )}

                {!data && !isLoading && (
                    <div className="sp-empty-state">
                        <p>Unable to load progress data.</p>
                    </div>
                )}
            </div>
        );
    };

    render() {
        const { milestoneStore } = this.props;

        return (
            <div className="sp-progress-container" data-test-milestone-progress-tab>
                {milestoneStore.models.length === 0 ? (
                    <div className="sp-empty-state">
                        <h3>No Milestones</h3>
                        <p>Create milestones in the Sprint Planning tab to see progress here.</p>
                    </div>
                ) : (
                    milestoneStore.models.map(m => this.renderMilestoneProgress(m as Milestone))
                )}
            </div>
        );
    }
}

export default MilestoneProgressTab;
